import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AgentRun, Conversation, LiveConversation, Message } from '@supporchat/shared';
import { api } from '../lib/api';
import { Avatar } from '../components/Avatar';
import { formatPhone } from '../components/CallBar';

interface Props {
  messageEvent: { conversationId: string; message: Message } | null;
  conversationEvent: Conversation | null;
  /** Ticket excluído: o card sai na hora, sem esperar a próxima atualização. */
  deletedEvent: { conversationId: string; at: number } | null;
  agentEvent: AgentRun | null;
  onOpenConversation: (conversationId: string) => void;
}

type Lens = 'todos' | 'bot' | 'humano' | 'fila';

const LENSES: Array<{ id: Lens; label: string }> = [
  { id: 'todos', label: 'Todos' },
  { id: 'bot', label: 'Assistente' },
  { id: 'humano', label: 'Atendentes' },
  { id: 'fila', label: 'Na fila' },
];

/** Quantos cards a tela acompanha. Acima disso ninguém lê mesmo. */
const CARD_LIMIT = 18;

/** Rede de segurança: evento perdido em reconexão não deixa a tela velha. */
const REFRESH_MS = 60_000;

/**
 * Acompanhamento ao vivo dos atendimentos em andamento.
 *
 * A caixa de entrada responde "o que preciso responder?"; esta tela responde
 * "o que está acontecendo agora?" — inclusive no que o assistente está fazendo
 * sozinho, que antes só dava para saber abrindo conversa por conversa.
 *
 * O primeiro desenho vem da API; daí em diante quem alimenta os cards são os
 * mesmos eventos de tempo real que o resto do painel já recebe, sem uma segunda
 * conexão nem enquete de servidor.
 */
export function MonitorPage({
  messageEvent,
  conversationEvent,
  deletedEvent,
  agentEvent,
  onOpenConversation,
}: Props) {
  const [cards, setCards] = useState<LiveConversation[]>([]);
  const [lens, setLens] = useState<Lens>('todos');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState<Record<string, AgentRun>>({});
  // Card que acabou de receber mensagem: pisca por alguns segundos.
  const [flashing, setFlashing] = useState<Record<string, number>>({});

  const load = useCallback(async () => {
    try {
      setCards(await api.liveConversations(CARD_LIMIT));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  // ── eventos de tempo real ──

  /**
   * Espelho dos cards para os handlers de evento.
   *
   * Decidir "esta conversa já está na tela?" dentro do updater do `setCards`
   * obrigaria a disparar a recarga de lá de dentro — e updater precisa ser
   * função pura, chamada mais de uma vez pelo React sem que isso signifique
   * duas recargas.
   */
  const cardsRef = useRef<LiveConversation[]>([]);
  cardsRef.current = cards;

  useEffect(() => {
    if (!messageEvent) return;
    const { conversationId, message } = messageEvent;

    // Mensagem de conversa que não está na tela: pode ser atendimento que
    // acabou de nascer. Recarregar é o caminho — o card ainda não existe aqui.
    if (!cardsRef.current.some((card) => card.conversation.id === conversationId)) {
      void load();
      return;
    }

    setCards((current) => {
      const index = current.findIndex((card) => card.conversation.id === conversationId);
      if (index < 0) return current;

      const card = current[index];
      const known = card.messages.some((item) => item.id === message.id);
      const messages = known
        ? card.messages.map((item) => (item.id === message.id ? message : item))
        : [...card.messages, message];

      // O card sobe: quem está falando agora é o que interessa ver primeiro.
      return [{ ...card, messages }, ...current.filter((_, position) => position !== index)];
    });

    setFlashing((current) => ({ ...current, [conversationId]: Date.now() }));
  }, [messageEvent, load]);

  useEffect(() => {
    if (!conversationEvent) return;

    const known = cardsRef.current.some((card) => card.conversation.id === conversationEvent.id);

    // Encerrado sai da tela: o monitor é dos atendimentos em andamento.
    if (conversationEvent.status === 'closed') {
      if (known) {
        setCards((current) => current.filter((card) => card.conversation.id !== conversationEvent.id));
      }
      return;
    }

    if (!known) {
      void load();
      return;
    }

    setCards((current) =>
      current.map((card) =>
        card.conversation.id === conversationEvent.id
          ? { ...card, conversation: conversationEvent }
          : card,
      ),
    );
  }, [conversationEvent, load]);

  useEffect(() => {
    if (!deletedEvent) return;
    setCards((current) => current.filter((card) => card.conversation.id !== deletedEvent.conversationId));
  }, [deletedEvent]);

  useEffect(() => {
    if (!agentEvent) return;
    setRuns((current) => ({ ...current, [agentEvent.conversationId]: agentEvent }));
  }, [agentEvent]);

  // Limpa o destaque sem depender de um timer por card.
  useEffect(() => {
    if (Object.keys(flashing).length === 0) return;
    const timer = window.setTimeout(() => {
      const cutoff = Date.now() - 4000;
      setFlashing((current) =>
        Object.fromEntries(Object.entries(current).filter(([, at]) => at > cutoff)),
      );
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [flashing]);

  const visible = useMemo(() => cards.filter((card) => matchesLens(card.conversation, lens)), [cards, lens]);

  const counts = useMemo(
    () => ({
      total: cards.length,
      bot: cards.filter((card) => matchesLens(card.conversation, 'bot')).length,
      humano: cards.filter((card) => matchesLens(card.conversation, 'humano')).length,
      fila: cards.filter((card) => matchesLens(card.conversation, 'fila')).length,
    }),
    [cards],
  );

  return (
    <div className="monitor">
      <header className="monitor__bar">
        <div className="monitor__filters">
          {LENSES.map((item) => (
            <button
              key={item.id}
              className="filter-chip"
              aria-pressed={lens === item.id}
              onClick={() => setLens(item.id)}
            >
              {item.label}
              {item.id !== 'todos' && <span className="monitor__count">{counts[item.id]}</span>}
            </button>
          ))}
        </div>

        <div className="monitor__status">
          <span className="monitor__live">
            <span className="monitor__pulse" aria-hidden="true" />
            ao vivo
          </span>
          <span className="muted">
            {counts.total} {counts.total === 1 ? 'atendimento em andamento' : 'atendimentos em andamento'}
          </span>
        </div>
      </header>

      {error && <div className="alert alert--error" style={{ margin: 12 }}>{error}</div>}

      {!loading && visible.length === 0 && (
        <div className="empty">
          {cards.length === 0
            ? 'Nenhum atendimento em andamento agora.'
            : 'Nenhum atendimento neste filtro.'}
        </div>
      )}

      <div className="monitor__grid">
        {visible.map((card) => (
          <LiveCard
            key={card.conversation.id}
            card={card}
            run={runs[card.conversation.id]}
            flashing={Boolean(flashing[card.conversation.id])}
            onOpen={() => onOpenConversation(card.conversation.id)}
          />
        ))}
      </div>
    </div>
  );
}

function LiveCard({
  card,
  run,
  flashing,
  onOpen,
}: {
  card: LiveConversation;
  run: AgentRun | undefined;
  flashing: boolean;
  onOpen: () => void;
}) {
  const { conversation, messages } = card;
  const streamRef = useRef<HTMLDivElement>(null);

  // O card mostra o fim da conversa: mensagem nova rola até ela.
  useEffect(() => {
    const node = streamRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages.length]);

  const name =
    conversation.contact.name ?? conversation.contact.pushName ?? conversation.contact.phone ?? 'Cliente';

  return (
    <article className={`live-card${flashing ? ' live-card--flash' : ''}`} data-state={cardState(conversation)}>
      <header className="live-card__head">
        <div className="live-card__identity">
          <Avatar
            kind="contact"
            id={conversation.contact.id}
            name={name}
            version={conversation.contact.avatarUpdatedAt}
            size="sm"
          />
          <span className="live-card__ticket mono">#{conversation.ticketNumber}</span>
          <span className="live-card__name">{name}</span>
          {conversation.contact.phone && (
            <span className="live-card__phone mono">{formatPhone(conversation.contact.phone)}</span>
          )}
        </div>
        <span className="live-card__who">{whoIsAnswering(conversation)}</span>
      </header>

      <div className="live-card__stream" ref={streamRef}>
        {messages.length === 0 && <div className="live-card__empty">Sem mensagens ainda.</div>}
        {messages.map((message) => (
          <div key={message.id} className={`live-line live-line--${message.sender}`}>
            <span className="live-line__author">{authorLabel(message)}</span>
            <span className="live-line__text">{messageText(message)}</span>
          </div>
        ))}
      </div>

      <footer className="live-card__foot">
        <span className="muted">{timeAgo(conversation.lastMessageAt)}</span>
        {conversation.reopenCount > 0 && (
          <span className="live-card__tag" title="Cliente já voltou depois de encerrado">
            {conversation.reopenCount}× reaberto
          </span>
        )}
        {run && conversation.handler === 'bot' && (
          <span className="live-card__tag" title={run.reason ?? undefined}>
            {agentLabel(run)}
          </span>
        )}
        <button className="btn btn--sm" onClick={onOpen}>
          Abrir
        </button>
      </footer>
    </article>
  );
}

function matchesLens(conversation: Conversation, lens: Lens): boolean {
  if (lens === 'todos') return true;
  if (lens === 'fila') return conversation.status === 'pending_human';
  if (lens === 'bot') return conversation.handler === 'bot' && conversation.status !== 'pending_human';
  return conversation.handler === 'human' && conversation.status !== 'pending_human';
}

function cardState(conversation: Conversation): 'bot' | 'human' | 'pending' {
  if (conversation.status === 'pending_human') return 'pending';
  return conversation.handler === 'human' ? 'human' : 'bot';
}

function whoIsAnswering(conversation: Conversation): string {
  if (conversation.status === 'pending_human') return 'Esperando atendente';
  if (conversation.handler === 'bot') return 'Assistente';
  return conversation.assignedUserName ?? 'Atendente';
}

function authorLabel(message: Message): string {
  if (message.sender === 'contact') return message.senderName ?? 'Cliente';
  if (message.sender === 'bot') return 'Assistente';
  if (message.sender === 'system') return 'Sistema';
  return message.senderName ?? 'Atendente';
}

/** Mídia sem legenda vira rótulo: o card mostra texto, não anexos. */
function messageText(message: Message): string {
  const content = message.content.trim();
  if (content) return content;

  const labels: Partial<Record<Message['type'], string>> = {
    image: '📷 Imagem',
    audio: message.transcriptionStatus === 'pending' ? '🎤 Áudio (transcrevendo...)' : '🎤 Áudio',
    video: '🎬 Vídeo',
    document: '📄 Documento',
    sticker: '🌟 Figurinha',
  };
  return labels[message.type] ?? 'Mensagem';
}

function agentLabel(run: AgentRun): string {
  const labels: Record<AgentRun['action'], string> = {
    replied: 'assistente respondeu',
    clarifying: 'assistente perguntou',
    courtesy: 'assistente cumprimentou',
    escalated: 'assistente transferiu',
    skipped: 'assistente não respondeu',
    failed: 'assistente falhou',
  };
  return labels[run.action];
}

function timeAgo(iso: string | null): string {
  if (!iso) return 'sem mensagens';

  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return 'agora há pouco';
  if (seconds < 3600) return `há ${Math.floor(seconds / 60)} min`;
  if (seconds < 86_400) return `há ${Math.floor(seconds / 3600)} h`;
  return `há ${Math.floor(seconds / 86_400)} d`;
}
